import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=logged in

  // Cookie-based session probe. If the httpOnly access_token cookie exists,
  // /auth/me will succeed. Otherwise the user is a guest.
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is normal (unauthenticated). Anything else we log for debugging.
      if (err?.response?.status !== 401) {
        console.warn("Auth bootstrap failed:", err?.message || err);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password, turnstileToken) => {
    try {
      const { data } = await api.post("/auth/login", {
        email, password,
        turnstile_token: turnstileToken || undefined,
      });
      if (data.twofa_required) {
        return { ok: false, twofa: { challenge_id: data.challenge_id, email_hint: data.email_hint } };
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const verifyTwoFA = useCallback(async (challengeId, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: challengeId, code });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) || formatApiErrorDetail(d) || e.message;
      return { ok: false, error: msg, reason: d?.reason, attempts_remaining: d?.attempts_remaining };
    }
  }, []);

  const register = useCallback(async (email, password, name, inviteCode, referralCode, turnstileToken, extras = {}) => {
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name,
        invite_code: inviteCode || undefined,
        referral_code: referralCode || undefined,
        affiliate_ref: extras.affiliate_ref || undefined,
        turnstile_token: turnstileToken || undefined,
        role: extras.role || undefined,
        firm_name: extras.firm_name || undefined,
        bar_number: extras.bar_number || undefined,
        bar_state: extras.bar_state || undefined,
      });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (cookies still cleared client-side):", err?.message || err);
    }
    setUser(false);
  }, []);

  const actions = useMemo(
    () => ({ login, register, logout, verifyTwoFA, refresh: bootstrap }),
    [login, register, logout, verifyTwoFA, bootstrap],
  );
  const value = useMemo(() => ({ user, ...actions }), [user, actions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
